import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  IconButton,
  HStack,
  Tabs,
  TabList,
  Tab,
  Text,
  VStack,
  Input,
  Textarea,
  useToast,
  Spinner,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Heading,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
} from '@chakra-ui/react';
import {
  Robot,
  PencilSimple,
  Check,
  X,
  MagnifyingGlass,
  ArrowClockwise,
  Trash,
  Plus,
  GearSix,
} from 'phosphor-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { PageBox } from '../../components/pageBox';
import { useBotConfig, IBotComConfig, TipoBot } from '../../hooks/useBotConfig';
import {
  BOT_AREAS,
  getBotAreaId,
  setBotArea,
  addArea,
  updateArea,
  deleteArea,
  BotAreaDef,
  getAreaBg,
  getAreaColorProps,
  TIPO_BOT_COLORS,
} from '../analiseBots/constants/botAreas';

// Usa TIPO_BOT_COLORS do botAreas (fonte unica)
const BOT_TYPE_COLORS = TIPO_BOT_COLORS;

interface EditingState {
  bot_name: string;
  tipo_bot: TipoBot;
  apelido: string;
  descricao: string;
}

export const BotConfigPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [editingBot, setEditingBot] = useState<EditingState | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen: isAreasOpen, onOpen: onAreasOpen, onClose: onAreasClose } = useDisclosure();
  const [newAreaLabel, setNewAreaLabel] = useState('');
  const [newAreaColor, setNewAreaColor] = useState('blue');
  const [editingArea, setEditingArea] = useState<BotAreaDef | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [areasVersion, setAreasVersion] = useState(0); // force re-render on area changes

  const { getTodosBots, updateConfig } = useBotConfig();

  const tipoBot: TipoBot | undefined = useMemo(() => {
    const tabMap: Record<number, TipoBot | undefined> = {
      0: undefined,
      1: 'RPA',
      2: 'ETL',
      3: 'WEBSCRAP',
    };
    return tabMap[selectedTab];
  }, [selectedTab]);

  const {
    data: bots,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['todosBots', { tipo_bot: tipoBot }],
    queryFn: getTodosBots,
    refetchInterval: 60000,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: EditingState) => {
      return updateConfig(
        data.tipo_bot,
        data.bot_name,
        {
          apelido: data.apelido || null,
          descricao: data.descricao || null,
        },
        true,
      ); // propagar_logs = true
    },
    onSuccess: result => {
      toast({
        title: 'Configuracao salva!',
        description: result.logs_atualizados
          ? `${result.logs_atualizados} logs atualizados`
          : 'Configuracao atualizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({ queryKey: ['todosBots'] });
      setEditingBot(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar',
        description: error?.response?.data?.detail?.message || 'Erro desconhecido',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['todosBots'] });
      toast({
        title: 'Atualizado!',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const startEditing = (bot: IBotComConfig) => {
    setEditingBot({
      bot_name: bot.bot_name,
      tipo_bot: bot.tipo_bot,
      apelido: bot.apelido || '',
      descricao: bot.descricao || '',
    });
  };

  const cancelEditing = () => {
    setEditingBot(null);
  };

  const saveEditing = () => {
    if (editingBot) {
      updateMutation.mutate(editingBot);
    }
  };

  const filteredBots =
    bots?.filter(
      bot =>
        bot.bot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bot.apelido?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  return (
    <PageBox>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack>
          <Robot size={24} />
          <Heading size="md">Configuracao de Bots</Heading>
        </HStack>
        <HStack spacing={2}>
          <InputGroup maxW="300px" size="sm">
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass size={16} color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nome ou apelido..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Button
            size="sm"
            leftIcon={<GearSix size={16} />}
            variant="outline"
            onClick={onAreasOpen}
          >
            Areas
          </Button>
          <Tooltip label="Atualizar lista">
            <IconButton
              aria-label="Atualizar"
              icon={<ArrowClockwise size={18} />}
              size="sm"
              colorScheme="green"
              onClick={handleRefresh}
              isLoading={isRefreshing || isFetching}
            />
          </Tooltip>
        </HStack>
      </Flex>

      <VStack spacing={4} align="stretch">
        {/* Tabs por tipo de bot */}
        <Tabs index={selectedTab} onChange={setSelectedTab} variant="enclosed">
          <TabList>
            <Tab>Todos</Tab>
            <Tab>
              <Badge colorScheme="purple" mr={2}>
                RPA
              </Badge>
              RPA
            </Tab>
            <Tab>
              <Badge colorScheme="green" mr={2}>
                ETL
              </Badge>
              ETL
            </Tab>
            <Tab>
              <Badge colorScheme="orange" mr={2}>
                WEB
              </Badge>
              WebScrap
            </Tab>
          </TabList>
        </Tabs>

        {/* Tabela de bots */}
        <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm">
          {isLoading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="lg" />
            </Flex>
          )}
          {!isLoading && filteredBots.length === 0 && (
            <Flex justify="center" align="center" py={10}>
              <Text color="gray.500">Nenhum bot encontrado</Text>
            </Flex>
          )}
          {!isLoading && filteredBots.length > 0 && (
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th width="100px">Tipo</Th>
                  <Th width="220px">Nome Original</Th>
                  <Th width="160px">Area</Th>
                  <Th width="200px">Apelido</Th>
                  <Th>Descricao</Th>
                  <Th width="100px" textAlign="center">
                    Acoes
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredBots.map(bot => {
                  const isEditing =
                    editingBot?.bot_name === bot.bot_name && editingBot?.tipo_bot === bot.tipo_bot;

                  return (
                    <Tr key={`${bot.tipo_bot}-${bot.bot_name}`} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <Badge colorScheme={BOT_TYPE_COLORS[bot.tipo_bot]}>{bot.tipo_bot}</Badge>
                      </Td>
                      <Td>
                        <Text fontFamily="mono" fontSize="sm">
                          {bot.bot_name}
                        </Text>
                      </Td>
                      <Td>
                        <Select
                          size="xs"
                          value={getBotAreaId(bot.bot_name)}
                          onChange={e => {
                            setBotArea(bot.bot_name, e.target.value || null);
                            // Force re-render
                            queryClient.invalidateQueries({ queryKey: ['todosBots'] });
                          }}
                          placeholder="Sem area"
                        >
                          {BOT_AREAS.map(area => (
                            <option key={area.id} value={area.id}>
                              {area.label}
                            </option>
                          ))}
                        </Select>
                      </Td>
                      <Td>
                        {isEditing ? (
                          <Input
                            size="sm"
                            value={editingBot.apelido}
                            onChange={e =>
                              setEditingBot({ ...editingBot, apelido: e.target.value })
                            }
                            placeholder="Digite o apelido..."
                            autoFocus
                          />
                        ) : (
                          <Text color={bot.apelido ? 'inherit' : 'gray.400'}>
                            {bot.apelido || '-'}
                          </Text>
                        )}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <Textarea
                            size="sm"
                            value={editingBot.descricao}
                            onChange={e =>
                              setEditingBot({ ...editingBot, descricao: e.target.value })
                            }
                            placeholder="Digite a descricao..."
                            rows={2}
                          />
                        ) : (
                          <Text color={bot.descricao ? 'inherit' : 'gray.400'} noOfLines={2}>
                            {bot.descricao || '-'}
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <HStack justify="center" spacing={1}>
                          {isEditing ? (
                            <>
                              <Tooltip label="Salvar">
                                <IconButton
                                  aria-label="Salvar"
                                  icon={<Check size={18} />}
                                  size="sm"
                                  colorScheme="green"
                                  onClick={saveEditing}
                                  isLoading={updateMutation.isPending}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar">
                                <IconButton
                                  aria-label="Cancelar"
                                  icon={<X size={18} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={cancelEditing}
                                  isDisabled={updateMutation.isPending}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip label="Editar">
                              <IconButton
                                aria-label="Editar"
                                icon={<PencilSimple size={18} />}
                                size="sm"
                                variant="ghost"
                                onClick={() => startEditing(bot)}
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Contador */}
        <Text fontSize="sm" color="gray.500">
          {filteredBots.length} bot(s) encontrado(s)
        </Text>
      </VStack>
      {/* Modal Gerenciar Areas */}
      <Modal
        isOpen={isAreasOpen}
        onClose={onAreasClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
          <Box bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <ModalHeader pb={3} pt={5} px={6}>
              <HStack spacing={3}>
                <Box p={2} bg="blue.50" borderRadius="lg">
                  <GearSix size={20} color="#3B82F6" weight="fill" />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="bold">
                    Gerenciar Areas
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="normal">
                    {BOT_AREAS.length} areas cadastradas
                  </Text>
                </VStack>
              </HStack>
            </ModalHeader>
            <ModalCloseButton top={5} right={5} borderRadius="full" />
          </Box>

          <ModalBody py={5} px={6}>
            <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
              {/* Coluna esquerda: lista de areas */}
              <Box flex={1}>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={3}
                >
                  Areas existentes
                </Text>
                <VStack spacing={1} align="stretch" maxH="400px" overflowY="auto">
                  {BOT_AREAS.map(area => {
                    const isEditingThis = editingArea?.id === area.id;
                    return (
                      <HStack
                        key={area.id}
                        justify="space-between"
                        px={3}
                        py={2}
                        borderRadius="lg"
                        bg={isEditingThis ? 'blue.50' : undefined}
                        border="1px solid"
                        borderColor={isEditingThis ? 'blue.200' : 'transparent'}
                        _hover={{
                          bg: isEditingThis ? 'blue.50' : 'gray.50',
                          borderColor: 'gray.200',
                        }}
                        transition="all 0.2s"
                      >
                        <HStack spacing={3} flex={1}>
                          <Box
                            w="14px"
                            h="14px"
                            borderRadius="full"
                            bg={getAreaBg(area.color)}
                            flexShrink={0}
                            boxShadow="0 0 0 2px white, 0 0 0 3px var(--chakra-colors-gray-200)"
                          />
                          <Text fontSize="sm" fontWeight="medium">
                            {area.label}
                          </Text>
                        </HStack>
                        <HStack spacing={0}>
                          <IconButton
                            aria-label="Editar"
                            icon={<PencilSimple size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme={isEditingThis ? 'blue' : 'gray'}
                            borderRadius="full"
                            onClick={() => {
                              if (isEditingThis) {
                                setEditingArea(null);
                                setNewAreaLabel('');
                              } else {
                                setEditingArea(area);
                                setNewAreaLabel(area.label);
                                setNewAreaColor(area.color);
                              }
                            }}
                          />
                          <IconButton
                            aria-label="Excluir"
                            icon={<Trash size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            borderRadius="full"
                            onClick={() => {
                              deleteArea(area.id);
                              setAreasVersion(v => v + 1);
                              if (editingArea?.id === area.id) {
                                setEditingArea(null);
                                setNewAreaLabel('');
                              }
                            }}
                          />
                        </HStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>

              {/* Divider vertical */}
              <Box w="1px" bg="gray.200" display={{ base: 'none', md: 'block' }} />

              {/* Coluna direita: criar/editar */}
              <Box flex={1}>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={3}
                >
                  {editingArea ? 'Editar area' : 'Nova area'}
                </Text>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="xs" color="gray.600" mb={1} fontWeight="medium">
                      Nome
                    </Text>
                    <Input
                      placeholder="Ex: Fiscal Federal"
                      value={newAreaLabel}
                      onChange={e => setNewAreaLabel(e.target.value)}
                      borderRadius="lg"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newAreaLabel.trim()) {
                          if (editingArea)
                            updateArea(editingArea.id, {
                              label: newAreaLabel.trim(),
                              color: newAreaColor,
                            });
                          else
                            addArea({
                              id: newAreaLabel.trim().toLowerCase().replace(/\s+/g, '-'),
                              label: newAreaLabel.trim(),
                              color: newAreaColor,
                            });
                          setNewAreaLabel('');
                          setNewAreaColor('blue');
                          setEditingArea(null);
                          setAreasVersion(v => v + 1);
                        }
                      }}
                    />
                  </Box>

                  {/* Color palette */}
                  <Box>
                    <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
                      Cor
                    </Text>
                    <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={2}>
                      {[
                        'green',
                        'blue',
                        'purple',
                        'teal',
                        'cyan',
                        'orange',
                        'yellow',
                        'gray',
                        'pink',
                        'red',
                      ].map(c => (
                        <Box
                          key={c}
                          h="32px"
                          borderRadius="lg"
                          bg={`${c}.400`}
                          cursor="pointer"
                          border="2px solid"
                          borderColor={newAreaColor === c ? 'gray.800' : 'transparent'}
                          boxShadow={
                            newAreaColor === c
                              ? '0 0 0 2px white, 0 0 0 4px var(--chakra-colors-gray-400)'
                              : 'none'
                          }
                          _hover={{ transform: 'scale(1.05)', zIndex: 1 }}
                          transition="all 0.15s"
                          onClick={() => setNewAreaColor(c)}
                        />
                      ))}
                    </Box>
                    {/* Cor personalizada */}
                    <HStack mt={3} spacing={3}>
                      <Box position="relative" overflow="hidden" borderRadius="lg" flexShrink={0}>
                        <Box
                          w="32px"
                          h="32px"
                          borderRadius="lg"
                          bg={getAreaBg(newAreaColor)}
                          border="2px solid"
                          borderColor="gray.200"
                        />
                        <input
                          type="color"
                          value={
                            newAreaColor.startsWith('custom-')
                              ? `#${newAreaColor.replace('custom-', '')}`
                              : '#3B82F6'
                          }
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: 'pointer',
                            width: '100%',
                            height: '100%',
                          }}
                          onChange={e =>
                            setNewAreaColor(`custom-${e.target.value.replace('#', '')}`)
                          }
                        />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {newAreaColor.startsWith('custom-')
                            ? `#${newAreaColor.replace('custom-', '')}`
                            : newAreaColor}
                        </Text>
                        <Text fontSize="10px" color="gray.400">
                          Clique no quadrado pra personalizar
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  {/* Preview */}
                  <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
                    <Text fontSize="10px" color="gray.400" mb={2}>
                      PREVIEW
                    </Text>
                    <Badge
                      {...getAreaColorProps(newAreaColor)}
                      px={4}
                      py={1.5}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      {newAreaLabel || 'Nome da area'}
                    </Badge>
                  </Box>

                  <HStack spacing={2}>
                    <Button
                      flex={1}
                      colorScheme="blue"
                      leftIcon={editingArea ? <Check size={16} /> : <Plus size={16} />}
                      isDisabled={!newAreaLabel.trim()}
                      borderRadius="lg"
                      onClick={() => {
                        if (editingArea)
                          updateArea(editingArea.id, {
                            label: newAreaLabel.trim(),
                            color: newAreaColor,
                          });
                        else
                          addArea({
                            id: newAreaLabel.trim().toLowerCase().replace(/\s+/g, '-'),
                            label: newAreaLabel.trim(),
                            color: newAreaColor,
                          });
                        setNewAreaLabel('');
                        setNewAreaColor('blue');
                        setEditingArea(null);
                        setAreasVersion(v => v + 1);
                      }}
                    >
                      {editingArea ? 'Salvar' : 'Adicionar'}
                    </Button>
                    {editingArea && (
                      <Button
                        variant="outline"
                        borderRadius="lg"
                        onClick={() => {
                          setEditingArea(null);
                          setNewAreaLabel('');
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </HStack>
                </VStack>
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageBox>
  );
};
